import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Armchair as Wheelchair, Car, Clock3, Plane, ShieldCheck, Stethoscope, Users, Wallet } from 'lucide-react';
import { scrollToTop } from '../utils/scrollUtils';

const features = [
  'Low-floor or ramp/lift-equipped vehicles',
  'Wheelchair restraints and secure seating systems',
  'Extra space for mobility equipment and essentials',
  'Trained drivers for assisted boarding and alighting',
];

const useCases = [
  {
    icon: Car,
    title: 'Daily local trips',
    description:
      'Book a wheelchair accessible taxi for shopping, social visits, work, or errands with reliable pickup windows.',
  },
  {
    icon: Stethoscope,
    title: 'Hospital and clinic visits',
    description:
      'Pre-book transport for appointments, treatment sessions, and discharge journeys where timing and support matter.',
  },
  {
    icon: Plane,
    title: 'Airport transfers',
    description:
      'Arrange airport journeys with enough time, luggage support, and a vehicle suited to your accessibility needs.',
  },
];

const bookingSteps = [
  'Enter your route and pickup time',
  'Add wheelchair and assistance requirements',
  'Confirm your booking and travel plan',
];

const faqs = [
  {
    question: 'How quickly can I book a wheelchair accessible taxi with AbleGo?',
    answer:
      'You can request a booking in minutes. For best availability, we recommend pre-booking your wheelchair accessible taxi as early as possible.',
  },
  {
    question: 'Are AbleGo drivers trained to assist wheelchair users?',
    answer:
      'Yes. Drivers are trained in safe assistance, including boarding, wheelchair securing, and respectful support throughout the journey.',
  },
  {
    question: 'Can I use AbleGo for regular medical transport?',
    answer:
      'Yes. Many passengers schedule repeat journeys for dialysis, physiotherapy, and other recurring appointments.',
  },
  {
    question: 'Is your wheelchair accessible taxi service available outside city centres?',
    answer:
      'Yes. AbleGo serves many urban and surrounding areas across the UK. Enter your route during booking to check availability.',
  },
  {
    question: 'Can I book a wheelchair accessible taxi for someone else?',
    answer:
      'Yes. You can arrange transport for a family member, client, or patient and include any mobility or assistance details in the booking.',
  },
  {
    question: 'What makes AbleGo different from a standard taxi service?',
    answer:
      'AbleGo focuses on accessible journeys first, with matched vehicles, trained support, and pre-booked reliability instead of uncertain on-demand availability.',
  },
];

const WheelchairAccessibleTaxiPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Wheelchair Accessible Taxi Booking | AbleGo UK';
    scrollToTop('instant');
  }, []);

  return (
    <>
      <Helmet>
        <title>Wheelchair Accessible Taxi Booking | AbleGo UK</title>
        <meta
          name="description"
          content="Book a wheelchair accessible taxi with ramps, secure seating and trained drivers. Arrange reliable UK transport with AbleGo today."
        />
        <link rel="canonical" href="https://ablego.co.uk/wheelchair-accessible-taxi" />
      </Helmet>

      <div className="pt-20">
        <section className="bg-gradient-to-br from-teal-700 via-teal-600 to-blue-700 text-white py-16 lg:py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl">
              <p className="text-teal-100 uppercase tracking-wide font-semibold mb-4">Fast, assisted booking</p>
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                Wheelchair Accessible Taxi Booking Made Simple
              </h1>
              <p className="text-lg lg:text-xl text-teal-50 max-w-3xl mb-8">
                Need a wheelchair accessible taxi now or later today? AbleGo helps you book quickly with the right vehicle, trained driver, and clear support.
              </p>
              <Link
                to="/booking"
                className="inline-flex items-center px-7 py-4 rounded-xl bg-white text-teal-700 font-semibold hover:bg-teal-50 transition-colors"
              >
                Book a Ride
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Built for safe wheelchair travel</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {features.map((feature) => (
                <article
                  key={feature}
                  className="rounded-xl border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800 p-6 flex gap-3 items-start"
                >
                  <Wheelchair className="w-6 h-6 text-teal-600 mt-0.5" />
                  <p className="text-gray-700 dark:text-gray-200">{feature}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50 dark:bg-dark-800">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Common wheelchair accessible taxi journeys</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {useCases.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className="p-6 rounded-2xl bg-white dark:bg-dark-900 border border-gray-100 dark:border-dark-700">
                    <Icon className="w-7 h-7 text-blue-600 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-3">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-14 bg-blue-600 text-white">
          <div className="container mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h2 className="text-3xl font-bold mb-3">Need a dependable ride instead of waiting for luck?</h2>
                <p className="text-blue-100">
                  Traditional taxi availability can vary. AbleGo focuses on pre-booked accessible journeys with the right vehicle confirmed in advance.
                </p>
              </div>
              <Link
                to="/booking"
                className="inline-flex justify-center items-center px-7 py-4 rounded-xl bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors"
              >
                Arrange Transport
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Why choose AbleGo over standard taxi booking?</h2>
            <div className="grid md:grid-cols-4 gap-5">
              <article className="rounded-xl border border-gray-200 dark:border-dark-700 p-5">
                <Clock3 className="w-6 h-6 text-teal-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-50 mb-2">Reliable timing</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Pre-booked pickups reduce uncertainty around availability.</p>
              </article>
              <article className="rounded-xl border border-gray-200 dark:border-dark-700 p-5">
                <ShieldCheck className="w-6 h-6 text-teal-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-50 mb-2">Accessibility-first</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Vehicle matching prioritises wheelchair safety and practical support.</p>
              </article>
              <article className="rounded-xl border border-gray-200 dark:border-dark-700 p-5">
                <Users className="w-6 h-6 text-teal-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-50 mb-2">Trained assistance</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Drivers support boarding, seating, and arrival with care.</p>
              </article>
              <article className="rounded-xl border border-gray-200 dark:border-dark-700 p-5">
                <Wallet className="w-6 h-6 text-teal-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-50 mb-2">Clear booking</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Set journey details once with no guesswork at pickup time.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50 dark:bg-dark-800">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Booking steps</h2>
            <ol className="space-y-4">
              {bookingSteps.map((step, index) => (
                <li key={step} className="p-5 rounded-xl bg-white dark:bg-dark-900 border border-gray-100 dark:border-dark-700">
                  <p className="text-sm font-semibold text-teal-600 mb-1">Step {index + 1}</p>
                  <p className="text-gray-700 dark:text-gray-200">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="py-16 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Frequently asked questions</h2>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <details key={faq.question} className="group rounded-xl border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800 p-5">
                  <summary className="cursor-pointer list-none font-semibold text-gray-900 dark:text-gray-50 flex justify-between items-center">
                    {faq.question}
                    <span className="ml-4 text-teal-600 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-gray-600 dark:text-gray-300 leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-900 text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Book your wheelchair accessible taxi with confidence</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Choose a service designed for accessibility, not a standard taxi workaround.
            </p>
            <Link
              to="/booking"
              className="inline-flex items-center px-8 py-4 rounded-xl bg-blue-500 text-white font-semibold hover:bg-blue-400 transition-colors"
            >
              Get Started
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default WheelchairAccessibleTaxiPage;
